import {
  FocusEventHandler,
  MouseEventHandler,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from "react";
import styles from "./MultiTextInput.module.scss";
import inputWrapperStyles from "../InputWrapper/InputWrapper.module.scss";
import InputWrapper, { InputWrapperProps } from "../InputWrapper/InputWrapper";
import { Close } from "../Icon/Icon";
import {
  autoUpdate,
  useFloating,
  useMergeRefs,
  flip,
  offset,
} from "@floating-ui/react";
import classNames from "classnames";

type MultiTextInputProps = {
  id?: string;
  value?: string[];
  placeholder?: string;
  onChange?: (value: string[]) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  defaultValue?: string;
  onClick?: MouseEventHandler<HTMLInputElement>;
} & Omit<InputWrapperProps, "children" | "inputRef">;

const MultiTextInput = forwardRef<HTMLInputElement, MultiTextInputProps>(
  function MultiTextInput(
    {
      id,
      className,
      leftSection,
      rightSection,
      onChange,
      onClick,
      onBlur,
      value,
      placeholder,
      disabled,
      size,
      unstyled,
      defaultValue,
      error,
    },
    ref,
  ) {
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(
      value ?? [],
    );

    const internalOnChange = onChange ?? setUncontrolledValue;
    const internalValue = value ?? uncontrolledValue;

    const innerRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState<string>("");

    const removeValue = useCallback(
      (index: number) => {
        internalOnChange(internalValue?.filter((_, i) => i !== index) ?? []);
        innerRef.current?.focus();
      },
      [internalOnChange, internalValue],
    );

    const addValue = useCallback(
      (value: string) => {
        internalOnChange([...internalValue, value]);
        setInputValue("");
        innerRef.current?.focus();
      },
      [internalOnChange, internalValue],
    );

    const onKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          addValue(inputValue);
        }

        if (e.key === "Backspace" && inputValue === "") {
          e.preventDefault();
          internalOnChange(internalValue.slice(0, internalValue.length - 1));
        }
      },
      [addValue, inputValue, internalOnChange, internalValue],
    );

    const { refs, floatingStyles } = useFloating<HTMLInputElement>({
      whileElementsMounted: autoUpdate,
      middleware: [flip(), offset(4)],
    });

    const inputRefs = useMergeRefs([ref, innerRef]);

    return (
      <InputWrapper
        className={classNames(styles.inputWrapper, className)}
        leftSection={leftSection}
        rightSection={rightSection}
        disabled={disabled}
        size={size}
        unstyled={unstyled}
        error={error}
        onClick={() => innerRef.current?.focus()}
      >
        <div className={styles.container} ref={refs.setReference}>
          {internalValue.map((v, index) => (
            <span className={styles.selectedItem} key={index}>
              <span>{v}</span>
              <div
                role="button"
                className={styles.closeButton}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(index);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    removeValue(index);
                  }
                }}
              >
                <Close size="xsmall" />
              </div>
            </span>
          ))}

          <input
            id={id}
            onClick={onClick}
            onKeyDown={onKeyDown}
            ref={inputRefs}
            className={classNames(
              inputWrapperStyles[size ?? "medium"],
              styles.input,
            )}
            type="text"
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={(e) => {
              onBlur?.(e);
            }}
            value={inputValue}
            placeholder={!value?.length ? placeholder : undefined}
            disabled={disabled}
            defaultValue={defaultValue}
          />
          <div
            style={{
              ...floatingStyles,
              maxWidth: refs.reference.current?.getBoundingClientRect().width,
            }}
            ref={refs.setFloating}
            className={classNames(styles.menu, {
              [styles.menuOpen]: inputValue.length > 0,
            })}
          >
            {inputValue.length > 0 && (
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => {
                  addValue(inputValue);
                }}
              >{`Create option "${inputValue}"`}</button>
            )}
          </div>
        </div>
      </InputWrapper>
    );
  },
);

export default MultiTextInput;
